import React, { useMemo } from 'react';
import { styled } from '@compiled/react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead?
import type { HeadType, RowType } from '@atlaskit/dynamic-table';
import CodeIcon from '@atlaskit/icon/glyph/code';
import SuitcaseIcon from '@atlaskit/icon/glyph/suitcase';
import { JiraIcon } from '@atlaskit/logo';
import { Inline } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';

import { DEFAULT_ITEM_TYPE, DEFAULT_SUBITEM_TYPE } from '../../../../common/constants';
import {
	JIRA_BUSINESS,
	JIRA_SOFTWARE,
	type SupportedThirdParty,
} from '../../../../common/constants/import-type';
import type { SupportedTool } from '../../../../common/constants/tools';
import type { ThirdPartyFieldConfigStateForItem } from '../../../../common/types';
import { renderIcon } from '../../../../common/ui/icon';
import { useImportTool } from '../../../../controllers/utils';
import { FieldSelector } from './field-selector';
import IncludeInImport from './include-in-import';
import messages from './messages';

const getFieldText = (thirdParty?: SupportedThirdParty) => {
	switch (thirdParty) {
		case JIRA_SOFTWARE:
			return {
				thirdPartyFieldText: messages.jiraSoftwareProjectFieldsSpork,
				importProjectFieldText: messages.jiraBusinessProjectFieldsSpork,
			};
		case JIRA_BUSINESS:
			return {
				thirdPartyFieldText: messages.jiraBusinessProjectFieldsSpork,
				importProjectFieldText: messages.jiraSoftwareProjectFieldsSpork,
			};
		default:
			return {
				thirdPartyFieldText: messages.thirdPartyFields,
				importProjectFieldText: messages.jwmFieldsSpork,
			};
	}
};

const getFieldIcons = (
	thirdPartyLabelText: string,
	importProjectLabelText: string,
	tool?: SupportedTool,
) => {
	switch (tool?.key) {
		case JIRA_SOFTWARE:
			return {
				thirdPartyIcon: <CodeIcon label={thirdPartyLabelText} size="medium" />,
				importProjectIcon: <SuitcaseIcon label={importProjectLabelText} size="medium" />,
			};
		case JIRA_BUSINESS:
			return {
				thirdPartyIcon: <SuitcaseIcon label={thirdPartyLabelText} size="medium" />,
				importProjectIcon: <CodeIcon label={importProjectLabelText} size="medium" />,
			};
		default:
			return {
				thirdPartyIcon: tool && renderIcon(tool?.icon, true),
				importProjectIcon: <JiraIcon size="small" appearance="brand" />,
			};
	}
};

export const useTableHead = (thirdParty: SupportedThirdParty) => {
	const { formatMessage } = useIntl();
	const tool = useImportTool(thirdParty);

	const tableHead = useMemo<HeadType>(() => {
		if (ff('jira-business-to-software-project-imports')) {
			const { thirdPartyFieldText, importProjectFieldText } = getFieldText(tool?.key);
			const thirdPartyFieldTextFormatted = formatMessage(thirdPartyFieldText, {
				thirdParty: tool?.name,
			});
			const importProjectFieldTextFormatted = formatMessage(importProjectFieldText);
			const { thirdPartyIcon, importProjectIcon } = getFieldIcons(
				thirdPartyFieldTextFormatted,
				importProjectFieldTextFormatted,
				tool,
			);

			return {
				cells: [
					{
						key: 'thirdPartyField',
						content: (
							<FieldWrapper>
								<Inline space="space.050" alignBlock="center">
									{thirdPartyIcon}
									{thirdPartyFieldTextFormatted}
								</Inline>
							</FieldWrapper>
						),
					},
					{
						key: 'arrow',
						width: 5,
					},
					{
						key: 'importProjectField',
						content: (
							<Inline space="space.050" alignBlock="center">
								{importProjectIcon}
								{importProjectFieldTextFormatted}
							</Inline>
						),
						width: 40,
					},
					{
						key: 'importToJira',
						content: <ColumnHeader>{formatMessage(messages.importToJira)}</ColumnHeader>,
						width: 25,
					},
				],
			};
		}
		const isInternalImport = thirdParty === JIRA_SOFTWARE && tool?.key === JIRA_SOFTWARE;

		const thirdPartyFieldText = isInternalImport
			? formatMessage(messages.jiraSoftwareProjectFieldsSpork)
			: formatMessage(messages.thirdPartyFields, {
					thirdParty: tool?.name,
				});

		const businessImportIcon = isInternalImport ? (
			<SuitcaseIcon label={thirdPartyFieldText} size="medium" />
		) : (
			<JiraIcon size="small" appearance="brand" />
		);

		return {
			cells: [
				{
					key: 'thirdPartyField',
					content: (
						<FieldWrapper>
							<Inline space="space.050" alignBlock="center">
								{isInternalImport ? (
									<CodeIcon label={thirdPartyFieldText} size="medium" />
								) : (
									tool && renderIcon(tool?.icon, true)
								)}
								{thirdPartyFieldText}
							</Inline>
						</FieldWrapper>
					),
				},
				{
					key: 'arrow',
					width: 5,
				},
				{
					key: 'jwmField',
					content: (
						<Inline space="space.050" alignBlock="center">
							{businessImportIcon}
							{formatMessage(
								isInternalImport
									? messages.jiraBusinessProjectFieldsSpork
									: messages.jwmFieldsSpork,
							)}
						</Inline>
					),
					width: 40,
				},
				{
					key: 'importToJira',
					content: <ColumnHeader>{formatMessage(messages.importToJira)}</ColumnHeader>,
					width: 25,
				},
			],
		};
	}, [formatMessage, tool, thirdParty]);

	return tableHead;
};

export const useTableRows = (
	thirdParty: SupportedThirdParty,
	thirdPartyFieldConfigState: ThirdPartyFieldConfigStateForItem,
) => {
	const { formatMessage } = useIntl();
	const { thirdPartyFieldMappings: fields, thirdPartyFieldOrder } = thirdPartyFieldConfigState;
	const tableRows = useMemo<RowType[] | undefined>(
		() =>
			thirdPartyFieldOrder.map((fieldId: string) => {
				const field = fields[fieldId];
				return {
					key: `row-${fieldId}`,
					cells: [
						{
							key: `${fieldId}-name`,
							content: (
								<FieldWrapper>
									{field.exampleValue
										? formatMessage(messages.exampleLabel, {
												fieldName: field.thirdPartyField.fieldName,
												exampleValue: field.exampleValue,
											})
										: field.thirdPartyField.fieldName}
								</FieldWrapper>
							),
						},
						{
							key: `${fieldId}-arrow`,
							content: '→',
						},
						{
							key: ff('jira-business-to-software-project-imports')
								? `${fieldId}-import-project-field`
								: `${fieldId}-jwm-field`,
							content: <FieldSelector thirdPartyFieldMapping={field} thirdParty={thirdParty} />,
						},
						{
							key: `${fieldId}-import`,
							content: <IncludeInImport thirdPartyFieldMapping={field} />,
						},
					],
				};
			}),
		[fields, formatMessage, thirdParty, thirdPartyFieldOrder],
	);

	return tableRows;
};

export const getItemTypeContent = (
	itemType: string,
	formatMessage: ReturnType<typeof useIntl>['formatMessage'],
) => {
	switch (itemType) {
		case DEFAULT_ITEM_TYPE:
			return formatMessage(messages.itemType);
		case DEFAULT_SUBITEM_TYPE:
			return formatMessage(messages.subitem);
		default:
			return itemType.toLowerCase();
	}
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled.div({
	marginLeft: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColumnHeader = styled.div({
	display: 'flex',
	alignItems: 'center',
	height: token('space.300', '24px'),
});
